import React, { useRef } from "react";
import { Icon } from "@/components/Icon";

/* CasinoShelf — the category row from the "03 Casino" redesign: a section header
   (icon chip + title + optional count + subtitle) with a "View all" link and
   left/right paging arrows, over a horizontal-scroll strip of CasinoTiles. */

export default function CasinoShelf({
  icon = "originals",
  title,
  count = null,
  subtitle = null,
  onViewAll = null,
  viewAllLabel = "View all",
  children,
  tileWidth = 168, // px scroll step
  testid,
}) {
  const scroller = useRef(null);
  const page = (dir) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * (tileWidth * 3 + 36), behavior: "smooth" });
  };

  return (
    <section className="space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-pnr-green/12 ring-1 ring-pnr-green/20">
            <Icon name={icon} size={18} className="text-pnr-green" />
          </span>
          <h2 className="truncate font-display text-xl font-black tracking-tight text-white sm:text-2xl">{title}</h2>
          {count != null && (
            <span className="shrink-0 rounded-md bg-surface-5 px-1.5 py-0.5 text-[11px] font-black tabular-nums text-[#a1a1aa] shadow-[inset_0_1px_3px_rgba(0,0,0,0.5)]">{count}</span>
          )}
          {subtitle && <span className="hidden text-xs font-medium text-[#71717a] sm:inline">{subtitle}</span>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {onViewAll && (
            <button onClick={onViewAll} className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-surface-border bg-surface-4 px-3 py-1.5 text-sm font-bold text-pnr-green transition-colors hover:border-pnr-green/50 hover:bg-pnr-green/10">
              {viewAllLabel} <Icon name="chevron-right" size={14} />
            </button>
          )}
          {/* Paging arrows are redundant on touch (swipe the strip) — desktop only. */}
          <button onClick={() => page(-1)} aria-label="Scroll left"
            className="hidden h-8 w-8 items-center justify-center rounded-full border border-surface-border bg-surface-3 text-[#a1a1aa] transition-colors hover:border-[#00D632]/50 hover:text-white sm:flex">
            <Icon name="chevron-left" size={16} />
          </button>
          <button onClick={() => page(1)} aria-label="Scroll right"
            className="hidden h-8 w-8 items-center justify-center rounded-full border border-surface-border bg-surface-3 text-[#a1a1aa] transition-colors hover:border-[#00D632]/50 hover:text-white sm:flex">
            <Icon name="chevron-right" size={16} />
          </button>
        </div>
      </div>
      {/* Optional green-fading rule under the header (desktop) — a quiet premium flourish. */}
      <div className="hidden h-px bg-gradient-to-r from-pnr-green/30 to-transparent lg:block" />
      <div ref={scroller} className="no-scrollbar flex gap-3 overflow-x-auto pb-1"
        style={{ scrollSnapType: "x proximity" }}>
        {React.Children.map(children, (c) => (
          <div style={{ width: tileWidth, scrollSnapAlign: "start" }} className="shrink-0">{c}</div>
        ))}
      </div>
    </section>
  );
}
